import React, { useEffect, useState } from 'react'
import Navbar from '../components/Navbar'
import { useAuth } from '../contexts/AuthContext'
import ControlCenterMap from '../components/controlCenter/ControlCenterMap'
import ControlCenterReports from '../components/controlCenter/ControlCenterReports'
import MunicipalityLicensesManager from '../components/controlCenter/MunicipalityLicensesManager'
import OperationConfigManager from '../components/controlCenter/OperationConfigManager'
import FilterPresetManager from '../components/controlCenter/FilterPresetManager'
import { useKioskRole } from '../hooks/useKioskRole'

/** Demo Reports UI — keep off for production until the runner ships. */
const SHOW_REPORTS_TAB = false

type ControlCenterTab = 'controlCenter' | 'reports' | 'management'
type ManagementSubTab = 'municipalities' | 'operationConfig' | 'filterPresets'

interface ControlCenterPageProps {
}

const ControlCenterPage: React.FC<ControlCenterPageProps> = () => {
  const { loading: authLoading, hasPermission } = useAuth()
  const kioskRole = useKioskRole()
  const isDisplayKiosk = kioskRole === 'display'

  const showManagement = hasPermission('controlCenterManager')
  const showTabBar = !isDisplayKiosk && (showManagement || SHOW_REPORTS_TAB)
  const [tab, setTab] = useState<ControlCenterTab>('controlCenter')
  const [managementSubTab, setManagementSubTab] = useState<ManagementSubTab>('municipalities')

  useEffect(() => {
    if (tab === 'management' && !showManagement) setTab('controlCenter')
    if (tab === 'reports' && !SHOW_REPORTS_TAB) setTab('controlCenter')
  }, [showManagement, tab])

  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    )
  }

  const news = ['Control Center app ready']
  const recentActivity = ['Operations overview and management tools']

  const shellHeight = kioskRole ? 'h-full max-h-full' : 'h-[100dvh] max-h-[100dvh]'

  return (
    <div className={`flex ${shellHeight} min-h-0 flex-col overflow-hidden bg-base-100`}>
      <div className="w-full shrink-0">
        <Navbar pageTitle="CONTROL CENTER" news={news} recentActivity={recentActivity} />
      </div>
      <main
        className={`flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden ${
          isDisplayKiosk
            ? 'px-0 py-0'
            : 'container mx-auto px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:py-4'
        }`}
      >
        {showTabBar ? (
          <div className="tabs tabs-lift mb-2 shrink-0 sm:mb-4">
            <input
              type="radio"
              name="control_center_tabs"
              role="tab"
              className="tab"
              aria-label="Control Center"
              checked={tab === 'controlCenter'}
              onChange={() => setTab('controlCenter')}
            />
            {SHOW_REPORTS_TAB ? (
              <input
                type="radio"
                name="control_center_tabs"
                role="tab"
                className="tab"
                aria-label="Reports"
                checked={tab === 'reports'}
                onChange={() => setTab('reports')}
              />
            ) : null}
            {showManagement ? (
              <input
                type="radio"
                name="control_center_tabs"
                role="tab"
                className="tab"
                aria-label="Management"
                checked={tab === 'management'}
                onChange={() => {
                  if (!showManagement) return
                  setTab('management')
                }}
              />
            ) : null}
          </div>
        ) : null}

        <div
          className={
            tab === 'controlCenter'
              ? 'flex min-h-0 w-full flex-1 flex-col overflow-hidden'
              : 'hidden'
          }
        >
          <ControlCenterMap active={tab === 'controlCenter'} />
        </div>

        {SHOW_REPORTS_TAB && tab === 'reports' ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ControlCenterReports />
          </div>
        ) : null}

        {tab === 'management' && showManagement ? (
          <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
            <div className="shrink-0 space-y-4">
              <div role="tablist" className="tabs tabs-boxed w-fit">
                <button
                  type="button"
                  role="tab"
                  className={`tab ${managementSubTab === 'municipalities' ? 'tab-active' : ''}`}
                  aria-selected={managementSubTab === 'municipalities'}
                  onClick={() => setManagementSubTab('municipalities')}
                >
                  Municipalities
                </button>
                <button
                  type="button"
                  role="tab"
                  className={`tab ${managementSubTab === 'operationConfig' ? 'tab-active' : ''}`}
                  aria-selected={managementSubTab === 'operationConfig'}
                  onClick={() => setManagementSubTab('operationConfig')}
                >
                  Operation Config
                </button>
                <button
                  type="button"
                  role="tab"
                  className={`tab ${managementSubTab === 'filterPresets' ? 'tab-active' : ''}`}
                  aria-selected={managementSubTab === 'filterPresets'}
                  onClick={() => setManagementSubTab('filterPresets')}
                >
                  Filter Presets
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {managementSubTab === 'municipalities' ? (
                <MunicipalityLicensesManager />
              ) : managementSubTab === 'operationConfig' ? (
                <OperationConfigManager />
              ) : (
                <FilterPresetManager />
              )}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  )
}

export default ControlCenterPage
